// ─── Empirical Narrative Engine ───────────────────────────────────────────────
// Maps hypotheses to findings and traces the research story from research
// question through to policy implication. Ensures findings answer questions
// and discussion reconciles evidence — not just report it.

// ── Hypothesis outcome determination ─────────────────────────────────────────

const OUTCOME_THRESHOLDS = {
  supported:          (c) => c.significant && Math.abs(c.beta) >= 0.20 && c.beta * (c._expectedSign ?? 1) > 0,
  partially_supported:(c) => (c.significant && c.beta * (c._expectedSign ?? 1) < 0) || (c.marginal && Math.abs(c.beta) >= 0.10),
  not_supported:      (c) => !c.significant,
}

function determineOutcome(coef, hypothesis) {
  if (!coef) return 'not_testable'
  const expectedSign = hypothesis?.direction === 'negative' ? -1 : 1
  const c = { ...coef, _expectedSign: expectedSign }
  if (OUTCOME_THRESHOLDS.supported(c)) return 'supported'
  if (OUTCOME_THRESHOLDS.partially_supported(c)) return 'partially_supported'
  return 'not_supported'
}

// ── Narrative templates by outcome ────────────────────────────────────────────

const OUTCOME_NARRATIVES = {
  supported: (hyp, coef, dv) =>
    `Hypothesis ${hyp.number || ''} (H${hyp.number || '?'}: ${hyp.text || `positive effect of ${hyp.iv} on ${dv}`}) receives empirical support. ` +
    `The regression analysis yields β = ${coef.beta} (SE = ${coef.std_err}, p = ${coef.p_value}), ` +
    `indicating that a one-unit increase in ${hyp.iv} is associated with a ${Math.abs(coef.beta)}-unit ${coef.beta > 0 ? 'increase' : 'decrease'} in ${dv}. ` +
    `The relationship is statistically significant and the direction is consistent with theoretical expectations.`,

  partially_supported: (hyp, coef, dv) => {
    if (coef.significant && coef.beta * (hyp.direction === 'negative' ? -1 : 1) < 0) {
      return `Hypothesis ${hyp.number || '?'} (${hyp.iv} → ${dv}) receives partial support in an unexpected direction. ` +
        `The coefficient is statistically significant (β = ${coef.beta}, p = ${coef.p_value}), but the direction is opposite to what was hypothesised. ` +
        `Rather than a positive effect, ${hyp.iv} shows a negative association with ${dv}. This finding, while not confirming the original hypothesis, ` +
        `is theoretically meaningful and warrants discussion in relation to domain-specific contradictions in the literature.`
    }
    return `Hypothesis ${hyp.number || '?'} (${hyp.iv} → ${dv}) is marginally supported (β = ${coef.beta}, p = ${coef.p_value}). ` +
      `The coefficient approaches but does not achieve conventional significance thresholds (p < .05). ` +
      `This result is interpreted as tentative support — the hypothesised relationship may exist but requires larger samples or refined measurement to achieve robust confirmation.`
  },

  not_supported: (hyp, coef, dv) =>
    `Hypothesis ${hyp.number || '?'} (${hyp.iv} → ${dv}) is not supported by the data (β = ${coef?.beta ?? 'N/A'}, p = ${coef?.p_value ?? 'N/A'}). ` +
    `This null finding should not be interpreted as evidence of absence. Rather, it may reflect measurement limitations, ` +
    `sample-specific characteristics, or the possibility that ${hyp.iv} operates through indirect pathways not captured by the present model. ` +
    `Null findings in social science research carry substantive significance and contribute to a more nuanced understanding of the phenomenon.`,

  not_testable: (hyp, _, dv) =>
    `Hypothesis ${hyp.number || '?'} could not be directly tested in the current model specification. Future research incorporating ${hyp.iv} as a measured construct is recommended.`,
}

// ── Research flow generator ────────────────────────────────────────────────────

/**
 * Build the empirical narrative connecting objectives → findings → implications.
 *
 * @param {{ hypotheses, olsResult, domain, thesis }} config
 * @returns {NarrativeFlow}
 */
export function buildEmpiricalNarrative(config) {
  const { hypotheses = [], olsResult, domain, thesis } = config
  if (!olsResult) return null

  const dvName = olsResult.dv
  const coefs = olsResult.coefficients || {}

  // Map each hypothesis to a finding
  const hypothesisOutcomes = hypotheses.map((hyp, idx) => {
    const hypNum = idx + 1
    const ivKey = hyp.iv || Object.keys(coefs).filter(k => k !== 'const')[idx] || `predictor_${idx + 1}`
    const coef = coefs[ivKey]
    const outcome = determineOutcome(coef, hyp)

    return {
      number: hypNum,
      hypothesis: hyp,
      iv: ivKey,
      coef,
      outcome,
      narrative: OUTCOME_NARRATIVES[outcome]?.(
        { ...hyp, number: hypNum, iv: ivKey },
        coef,
        dvName
      ) || '',
    }
  })

  // If no hypotheses defined, build from coefficients
  if (hypotheses.length === 0) {
    const coefEntries = Object.entries(coefs).filter(([k]) => k !== 'const')
    coefEntries.forEach(([ivName, coef], idx) => {
      const pseudoHyp = { number: idx + 1, iv: ivName, text: `positive effect of ${ivName} on ${dvName}`, direction: 'positive' }
      const outcome = determineOutcome(coef, pseudoHyp)
      hypothesisOutcomes.push({
        number: idx + 1,
        hypothesis: pseudoHyp,
        iv: ivName,
        coef,
        outcome,
        narrative: OUTCOME_NARRATIVES[outcome]?.(pseudoHyp, coef, dvName) || '',
      })
    })
  }

  // Summary counts
  const supported          = hypothesisOutcomes.filter(h => h.outcome === 'supported')
  const partiallySupported = hypothesisOutcomes.filter(h => h.outcome === 'partially_supported')
  const notSupported       = hypothesisOutcomes.filter(h => h.outcome === 'not_supported')

  // Overall model narrative
  const modelNarrative = buildModelNarrative(olsResult, supported, partiallySupported, notSupported, dvName, domain)

  // Research question linkage
  const rqLinkage = buildRQLinkage(thesis, olsResult, supported, domain)

  // Implication generation
  const implications = buildImplications(hypothesisOutcomes, domain, thesis)

  return {
    hypothesisOutcomes,
    supportedCount:          supported.length,
    partiallySupportedCount: partiallySupported.length,
    notSupportedCount:       notSupported.length,
    modelNarrative,
    rqLinkage,
    implications,
    researchCoherence: assessCoherence(olsResult, hypothesisOutcomes),
  }
}

function buildModelNarrative(ols, supported, partial, notSup, dvName, domain) {
  const total = supported.length + partial.length + notSup.length
  const supportRate = total > 0 ? supported.length / total : 0

  if (supportRate > 0.8) {
    return `The regression model provides strong overall support for the theoretical framework (R² = ${ols.r_squared}, F = ${ols.f_statistic}, p = ${ols.f_pvalue}). ` +
      `However, the near-universal confirmation of hypotheses should prompt critical reflection on the realism of these results and the representativeness of the sample.`
  }
  if (supportRate > 0.5) {
    return `The regression model accounts for ${Math.round(ols.r_squared * 100)}% of variance in ${dvName} (R² = ${ols.r_squared}, adj. R² = ${ols.adj_r_squared}, F = ${ols.f_statistic}, p = ${ols.f_pvalue}). ` +
      `${supported.length} of ${total} hypotheses receive full support, ${partial.length} receive partial support, and ${notSup.length} are not confirmed — a finding pattern consistent with the complexity of the ${domain.replace(/_/g, ' ')} context.`
  }
  return `The regression model (R² = ${ols.r_squared}, F = ${ols.f_statistic}, p = ${ols.f_pvalue}) yields a mixed findings pattern: ` +
    `${notSup.length} of ${total} hypotheses are not supported. This is substantively important. ` +
    `Null and contradictory findings reflect the institutional complexity of the setting and contribute a more differentiated understanding than blanket confirmation would provide.`
}

function buildRQLinkage(thesis, ols, supported, domain) {
  const title = thesis?.title || 'this study'
  const rqAnswer = supported.length > 0
    ? `The analysis confirms that ${supported.map(h => h.iv).join(' and ')} are statistically significant predictors of ${ols.dv}, partially answering the central research question of ${title}.`
    : `The analysis does not identify consistently strong predictors of ${ols.dv}, suggesting that the research question posed in ${title} may require reformulation or that the phenomenon is driven by factors beyond those captured in the present model.`

  return rqAnswer
}

function buildImplications(outcomes, domain, thesis) {
  const policy = []
  const theoretical = []
  const methodological = []

  const sigOuts = outcomes.filter(h => h.outcome === 'supported')
  const nullOuts = outcomes.filter(h => h.outcome === 'not_supported')

  if (sigOuts.length > 0) {
    policy.push(`Significant predictors (${sigOuts.map(h => h.iv).join(', ')}) should be prioritised in policy interventions targeting ${outcomes[0]?.coef ? outcomes[0].iv : 'the outcome'}.`)
    theoretical.push(`The findings partially validate theoretical frameworks that foreground these dimensions as consequential, contributing to cumulative knowledge in the ${domain.replace(/_/g, ' ')} domain.`)
  }
  if (nullOuts.length > 0) {
    policy.push(`Non-significant predictors (${nullOuts.map(h => h.iv).join(', ')}) should not be assumed irrelevant — they may operate through mediating mechanisms not modelled in the present study.`)
    theoretical.push(`Null findings challenge assumptions of straightforward linear relationships and call for more contextually sensitive theoretical models.`)
    methodological.push(`Future research should consider mediating variables, interaction effects, or non-linear specifications to capture the complexity underlying these null relationships.`)
  }
  methodological.push(`Cross-sectional data limits causal inference. Longitudinal or quasi-experimental designs are required to establish temporal precedence.`)

  return { policy, theoretical, methodological }
}

function assessCoherence(ols, outcomes) {
  const warnings = []
  if (ols.r_squared > 0 && outcomes.filter(h => h.outcome === 'not_supported').length === outcomes.length) {
    warnings.push('All hypotheses are unsupported despite a significant overall model — check for suppressor effects or multicollinearity.')
  }
  if (outcomes.filter(h => h.outcome === 'supported').length === outcomes.length && outcomes.length > 3) {
    warnings.push('All hypotheses supported — this is an unusually clean result. Verify data generation parameters and plausibility scores.')
  }
  return { warnings, coherent: warnings.length === 0 }
}
